import { createReference, MedplumClient, WithId } from "@medplum/core";
import type { Flag } from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";

import { joinReferenceString } from "@/utils/medplum";
import { isMedplumNotFoundError } from "@/utils/medplum-forbidden";
import { ifMatchVersionOptions } from "@/utils/medplum-if-match";
import { isMedplumIfMatchConflictError } from "@/utils/medplum-if-match-conflict";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { FlagFailure } from "../constants/flag-failures";
import { buildRemovedClientFlagResource, getLibraryFlagId } from "../utils/client-flag";
import { getClientFlagRemoval } from "../utils/flag-archive";
import { getLibraryFlagCoding } from "../utils/practice-flag";

/**
 * Taking a library flag back off a client's chart.
 *
 * Its own module rather than another export on `./client-flags`, which is already past the
 * 350-line refactor trigger — see `refactor-trigger-thresholds.mdc`.
 */

/**
 * `LookupFailed` is the read of the flag being removed or of its library entry. `NotFound` means it
 * is not on this chart — gone, or an id from another chart. `WriteFailed` leaves the flag in place,
 * so a retry is safe.
 *
 * There is no `AccountsFailed`: the flag is already in the practice compartment and the update
 * carries its `meta` across.
 */
export type RemoveClientFlagFailure =
  | FlagFailure.LookupFailed
  | FlagFailure.NotFound
  | FlagFailure.WriteFailed;

export type RemoveClientFlagResult =
  | { ok: true; data: WithId<Flag> }
  | { ok: false; reason: RemoveClientFlagFailure };

/**
 * Removes one applied flag from a client's chart.
 *
 * Deactivates rather than deletes, mirroring a library entry's delete. The resource is what the
 * chart's flag archive reads, so erasing it would erase the record of what was once flagged — and
 * the PHI rules ask for the reversible shape regardless.
 *
 * The flag is re-read and its `subject` checked against this client before anything is written.
 * Ids reach here from the drawer's own rows, so a mismatch means something is mis-wired rather than
 * malicious — but this is a write, and a foreign chart's `Flag` reaching it would be a cross-tenant
 * write. It reads as `NotFound`, because from this chart's side there is no such flag.
 *
 * The flag's library entry is read fresh before the write, and its name, category and priority are
 * frozen onto the removed flag — see `buildRemovedClientFlagResource`. Fresh rather than taken from
 * the drawer's row, so a drawer opened before a library edit cannot freeze stale values. Without the
 * entry there is nothing to freeze, so a failed read writes nothing and reports `LookupFailed`.
 *
 * Idempotent: removing a flag that an earlier removal already recorded writes nothing and reports
 * success, so a double-click or a stale drawer cannot turn into an error the user has no way to act
 * on. The earlier removal's record is left as it stands rather than being restamped. A flag that is
 * inactive without that record gets the full removal write, so it reaches the archive.
 *
 * The write carries `If-Match` with the version that was read, so two removals racing on one flag
 * cannot both write. The one that loses the race re-reads the flag and settles the same way as the
 * already-removed path — see `settleConflictingRemoval`.
 *
 * Never throws. Every step that can reject is captured into a `reason`, so callers can drive the UI
 * from the result alone.
 *
 * @param flagId - Logical id of the applied `Flag`, as the drawer's row carries it
 * @param clientId - Patient logical id whose chart it must belong to
 * @param reason - Why the clinician removed it — a listed reason, or their own words for `Other` —
 *   or `undefined` when none was given. PHI: it is never logged, never attached to a Sentry
 *   report, and never put in an error message.
 * @returns `{ ok: true, data }` with the deactivated Flag, or `{ ok: false, reason }`
 */
export async function removeClientFlag({
  medplum,
  flagId,
  clientId,
  reason,
}: {
  medplum: MedplumClient;
  flagId: string;
  clientId: string;
  reason?: string;
}): Promise<RemoveClientFlagResult> {
  let existing: WithId<Flag>;
  try {
    existing = await medplum.readResource("Flag", flagId, { cache: "no-cache" });
  } catch (error) {
    if (isMedplumNotFoundError(error)) {
      return { ok: false, reason: FlagFailure.NotFound };
    }

    captureExceptionWithoutPhi("Failed to read the client flag being removed", error, { flagId });
    return { ok: false, reason: FlagFailure.LookupFailed };
  }

  if (existing.subject?.reference !== joinReferenceString("Patient", clientId)) {
    return { ok: false, reason: FlagFailure.NotFound };
  }

  if (existing.status === "inactive" && hasRemovalRecord(existing)) {
    return { ok: true, data: existing };
  }

  const profile = medplum.getProfile();

  // Nothing identifies the remover without a profile, and the archive's Provider column would be
  // blank forever. A signed-in session always has one, so this is a broken client rather than a
  // state to write through.
  if (!profile) {
    Sentry.captureException(new Error("Cannot remove a client flag without an acting profile"));
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  const libraryFlagId = getLibraryFlagId(existing);

  // Every flag the drawer lists resolved against a library entry, so a flag with no link cannot
  // reach here through the UI.
  if (!libraryFlagId) {
    Sentry.captureException(new Error("Cannot remove a client flag with no library entry"), {
      extra: { flagId },
    });
    return { ok: false, reason: FlagFailure.LookupFailed };
  }

  let libraryFlag: WithId<Flag>;
  try {
    libraryFlag = await medplum.readResource("Flag", libraryFlagId, { cache: "no-cache" });
  } catch (error) {
    captureExceptionWithoutPhi("Failed to read the removed client flag's library entry", error, {
      flagId,
      libraryFlagId,
    });
    return { ok: false, reason: FlagFailure.LookupFailed };
  }

  let flag: WithId<Flag>;
  try {
    flag = await medplum.updateResource(
      buildRemovedClientFlagResource(existing, libraryFlag, {
        reason,
        removedAt: new Date().toISOString(),
        removedBy: createReference(profile).reference!,
      }),
      ifMatchVersionOptions(existing),
    );
  } catch (error) {
    if (isMedplumIfMatchConflictError(error)) {
      return settleConflictingRemoval(medplum, flagId);
    }

    // A rejected write's OperationOutcome can quote the resource sent, reason included.
    captureExceptionWithoutPhi("Failed to remove a client flag", error, { flagId });
    return { ok: false, reason: FlagFailure.WriteFailed };
  }

  medplum.invalidateSearches("Flag");

  return { ok: true, data: flag };
}

/**
 * Settles a removal whose write was refused because the flag changed after it was read.
 *
 * When the change was another removal, that removal's record stands and this one reports success,
 * the same as removing a flag that is already inactive. A removal is recognized by what it writes —
 * see `hasRemovalRecord` — not by the status alone. Any other change reports `WriteFailed`, and the
 * retry works from the flag as it is now.
 *
 * @param flagId - Logical id of the applied `Flag` whose write was refused
 * @returns `{ ok: true, data }` with the flag the other removal wrote, or `WriteFailed`
 */
async function settleConflictingRemoval(
  medplum: MedplumClient,
  flagId: string,
): Promise<RemoveClientFlagResult> {
  try {
    const current = await medplum.readResource("Flag", flagId, { cache: "no-cache" });

    if (current.status === "inactive" && hasRemovalRecord(current)) {
      medplum.invalidateSearches("Flag");
      return { ok: true, data: current };
    }
  } catch (error) {
    captureExceptionWithoutPhi("Failed to re-read a client flag after a removal conflict", error, {
      flagId,
    });
  }

  return { ok: false, reason: FlagFailure.WriteFailed };
}

/**
 * True when an inactive flag carries everything a removal writes: when it was removed, who removed
 * it, and the frozen name the archive shows. A flag made inactive any other way lacks them, and the
 * archive would leave it out.
 *
 * @param flag - An inactive flag, as read before the write or re-read after a refused one
 */
function hasRemovalRecord(flag: Flag): boolean {
  const { removedAt, removedByReference } = getClientFlagRemoval(flag);

  return Boolean(removedAt && removedByReference && getLibraryFlagCoding(flag)?.display);
}
